export const worldHistoryCourse = {
  id: "world-history-2024",
  title: "World History: Civilizations, Empires & Global Connections",
  shortDescription: "Journey through human civilization from ancient times to the modern era, exploring major cultures, empires, and historical transformations.",
  description: "Comprehensive survey of world history covering major civilizations, cultural developments, political systems, and global interactions from prehistory to the contemporary world. Students will analyze historical sources, understand cause-and-effect relationships, and develop critical thinking skills about the human past.",
  category: "Arts & Humanities",
  industryCategory: "Arts & Humanities",
  difficulty: "beginner",
  duration: 15,
  estimatedWeeks: 15,
  instructorName: "Workazon Platform",
  instructorId: "platform",
  instructorBio: "Expert-curated content from history professors and world civilization specialists",
  thumbnailUrl: "https://images.unsplash.com/photo-1461360228754-6e81c478b882?w=400",
  price: 0,
  isPublic: true,
  status: "published",
  template: "comprehensive",
  version: "1.0",
  createdBy: "platform",
  createdAt: new Date(),
  lastModified: new Date(),
  updatedAt: new Date(),

  learningObjectives: [
    "Understand major developments in world civilizations",
    "Analyze causes and consequences of historical events",
    "Evaluate primary and secondary historical sources",
    "Compare different cultural and political systems",
    "Examine patterns of trade, migration, and cultural exchange",
    "Understand the impact of technology on historical development",
    "Analyze the formation and decline of empires",
    "Evaluate the legacy of historical events on the modern world"
  ],

  prerequisites: [
    "High school education",
    "Basic reading comprehension",
    "Interest in history and cultures"
  ],

  skills: [
    "Historical Analysis",
    "Critical Thinking",
    "Source Evaluation",
    "Research Methods",
    "Writing Skills",
    "Cultural Awareness",
    "Chronological Thinking",
    "Comparative Analysis"
  ],

  marketData: {
    averageSalary: 48000,
    salaryRange: {
      min: 32000,
      max: 70000
    },
    jobDemand: 3.2,
    marketTrend: "stable",
    careerPaths: [
      "History Teacher",
      "Museum Curator",
      "Archivist",
      "Research Assistant",
      "Cultural Resource Manager",
      "Historical Writer"
    ],
    relatedSkills: [
      "Research",
      "Writing",
      "Analysis",
      "Teaching",
      "Cultural Knowledge",
      "Digital Humanities"
    ]
  },

  enrollmentCount: 0,
  completionRate: 0,
  averageRating: 0,
  reviewCount: 0,

  modules: [
    {
      id: "ancient-civilizations",
      title: "Ancient Civilizations and Early Societies",
      description: "Exploring the rise of the first civilizations in Mesopotamia, Egypt, India, and China",
      order: 1,
      estimatedHours: 4.0,
      content: [
        {
          id: "civilization-intro",
          title: "What Makes a Civilization?",
          description: "Characteristics and development of early human civilizations",
          type: "video",
          duration: 30
        },
        {
          id: "river-valley-civilizations",
          title: "River Valley Civilizations",
          description: "The birth of civilization along major river systems",
          type: "text",
          duration: 45,
          textContent: `# River Valley Civilizations

## The Cradle of Human Civilization

### Defining Civilization

Civilization represents a complex form of human organization characterized by specific features that distinguish it from earlier hunter-gatherer societies.

**Key Features of Civilization:**
- **Cities**: Large, permanent settlements with complex organization
- **Government**: Formal political structures and laws
- **Religion**: Organized belief systems and institutions
- **Social Structure**: Distinct social classes and roles
- **Writing**: Systems for recording information
- **Art**: Sophisticated cultural expression
- **Technology**: Advanced tools and techniques

### Geographic Foundations

**Why River Valleys?**
River valleys provided the essential conditions for early civilizations:
- **Fertile Soil**: Annual flooding deposited nutrient-rich sediment
- **Water Supply**: Reliable water for agriculture and consumption
- **Transportation**: Rivers served as highways for trade and communication
- **Natural Barriers**: Geographic features provided protection

### Mesopotamian Civilization (3500-539 BCE)

**Geographic Setting**
- Located between the Tigris and Euphrates rivers
- Modern-day Iraq and surrounding regions
- Known as the "Cradle of Civilization"

**Major Achievements**
- **Writing**: Cuneiform script (c. 3200 BCE)
- **Law**: Code of Hammurabi (c. 1750 BCE)
- **Mathematics**: Base-60 number system, geometry
- **Architecture**: Ziggurats, arch construction
- **Literature**: Epic of Gilgamesh

**Political Development**
- **City-States**: Independent urban centers (Ur, Babylon, Assyria)
- **Kingship**: Divine authority of rulers
- **Empires**: Successive conquests and unifications

**Social Structure**
- **Nobles and Priests**: Elite ruling class
- **Merchants and Artisans**: Middle class
- **Farmers**: Majority population
- **Slaves**: Lowest social level

### Egyptian Civilization (3100-30 BCE)

**Geographic Advantages**
- Nile River valley and delta
- Predictable annual flooding
- Natural barriers: deserts, cataracts, Mediterranean Sea

**Political Organization**
- **Pharaoh**: Divine king, central authority
- **Unified Kingdom**: Upper and Lower Egypt united (c. 3100 BCE)
- **Dynasties**: 31 ruling families over 3,000 years

**Cultural Achievements**
- **Architecture**: Pyramids, temples, obelisks
- **Art**: Sophisticated painting and sculpture
- **Writing**: Hieroglyphic system
- **Medicine**: Advanced surgical and medical knowledge
- **Astronomy**: Calendar, star charts

**Social Structure**
- **Pharaoh**: Divine ruler at apex
- **Nobles and Priests**: Administrative and religious elite
- **Scribes**: Educated bureaucrats
- **Artisans and Merchants**: Skilled workers
- **Farmers**: Majority of population
- **Slaves**: Prisoners of war, debtors

**Religious Beliefs**
- **Polytheism**: Multiple gods with specific roles
- **Afterlife**: Elaborate burial practices, mummification
- **Ma'at**: Concept of cosmic order and justice

### Indus Valley Civilization (2600-1900 BCE)

**Geographic Extent**
- Indus River valley (modern Pakistan and northwest India)
- Over 1,400 sites discovered
- Major cities: Harappa, Mohenjo-daro

**Urban Planning**
- **Grid System**: Well-planned streets and districts
- **Sanitation**: Advanced sewage and water systems
- **Standardization**: Uniform weights, measures, and bricks

**Economic Features**
- **Agriculture**: Wheat, barley, peas, sesame
- **Crafts**: Bronze working, pottery, textiles
- **Trade**: Extensive networks reaching Mesopotamia

**Mysterious Aspects**
- **Undeciphered Script**: Writing system not yet understood
- **No Obvious Rulers**: Lack of palaces or royal burials
- **Peaceful Society**: Little evidence of warfare
- **Sudden Decline**: Reasons for abandonment unclear

### Chinese Civilization (2070 BCE-present)

**Geographic Features**
- Yellow River (Huang He) valley
- Yangtze River region
- Natural barriers: mountains, deserts, ocean

**Dynastic Development**
- **Xia Dynasty** (2070-1600 BCE): Legendary first dynasty
- **Shang Dynasty** (1600-1046 BCE): First historically confirmed
- **Zhou Dynasty** (1046-256 BCE): Longest-lasting dynasty

**Cultural Innovations**
- **Writing**: Chinese characters (logographic system)
- **Philosophy**: Confucianism, Taoism, Legalism
- **Technology**: Bronze casting, silk production, paper
- **Government**: Mandate of Heaven concept

**Social Organization**
- **Emperor**: Son of Heaven, absolute ruler
- **Nobles**: Aristocratic landowners
- **Scholars**: Educated bureaucrats
- **Farmers**: Respected producers
- **Artisans**: Skilled craftspeople
- **Merchants**: Lowest social class

### Comparative Analysis

**Similarities Across Civilizations**
- River valley locations
- Agricultural foundations
- Urban development
- Social stratification
- Religious institutions
- Written languages
- Trade networks

**Differences in Development**
- **Political Organization**: City-states vs. unified kingdoms
- **Religious Systems**: Polytheism vs. philosophical traditions
- **Social Mobility**: Rigid vs. flexible class systems
- **Geographic Isolation**: Open vs. protected territories

### Environmental Challenges

**Common Problems**
- **Flooding**: Both destructive and beneficial
- **Soil Salinization**: Irrigation-related problems
- **Climate Change**: Droughts and environmental shifts
- **Resource Depletion**: Overuse of natural resources

**Adaptive Strategies**
- **Irrigation Systems**: Canals, dams, water management
- **Crop Rotation**: Maintaining soil fertility
- **Food Storage**: Granaries and preservation techniques
- **Trade Networks**: Obtaining resources from distant regions

### Legacy and Influence

**Technological Innovations**
- Writing systems
- Mathematical concepts
- Agricultural techniques
- Architectural methods
- Metallurgy

**Social and Political Concepts**
- Legal codes
- Administrative systems
- Urban planning
- Religious institutions

**Cultural Contributions**
- Literature and art
- Philosophical traditions
- Educational systems
- Calendar systems

The river valley civilizations established the foundations of human civilization, creating innovations and institutions that continue to influence the modern world. Their successes and failures provide valuable lessons about the relationship between environment, technology, and social organization.`
        },
        {
          id: "historical-timeline",
          title: "Interactive Historical Timeline",
          description: "Build and explore timelines of ancient civilizations",
          type: "arts-humanities-interactive",
          duration: 25,
          interactiveData: {
            exerciseType: "timeline-construction",
            instructions: "Create chronological timelines comparing the development of different ancient civilizations and identify key events and innovations.",
            timelineData: [
              {
                civilization: "Mesopotamia",
                events: [
                  { date: "3500 BCE", event: "First cities emerge" },
                  { date: "3200 BCE", event: "Cuneiform writing developed" },
                  { date: "2334 BCE", event: "Sargon creates first empire" },
                  { date: "1750 BCE", event: "Code of Hammurabi" }
                ]
              },
              {
                civilization: "Egypt",
                events: [
                  { date: "3100 BCE", event: "Upper and Lower Egypt unified" },
                  { date: "2600 BCE", event: "Great Pyramid built" },
                  { date: "1550 BCE", event: "New Kingdom begins" },
                  { date: "30 BCE", event: "Roman conquest" }
                ]
              },
              {
                civilization: "Indus Valley",
                events: [
                  { date: "2600 BCE", event: "Mature Harappan period begins" },
                  { date: "2000 BCE", event: "Urban decline starts" },
                  { date: "1900 BCE", event: "Major cities abandoned" }
                ]
              }
            ],
            hints: [
              "Look for patterns across civilizations",
              "Consider how geography influenced development",
              "Think about cause and effect relationships"
            ]
          }
        }
      ],
      quiz: {
        id: "ancient-civilizations-quiz",
        title: "Ancient Civilizations Quiz",
        description: "Test your knowledge of early river valley civilizations",
        timeLimit: 20,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Which river valley civilization developed the first known writing system?",
            options: [
              "Egyptian",
              "Mesopotamian",
              "Indus Valley",
              "Chinese"
            ],
            correctAnswer: 1,
            explanation: "Mesopotamians developed cuneiform writing around 3200 BCE, making it the earliest known writing system in human history."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "What was the main advantage of river valley locations for early civilizations?",
            options: [
              "Mountain protection",
              "Fertile soil from flooding",
              "Ocean access",
              "Forest resources"
            ],
            correctAnswer: 1,
            explanation: "River valleys provided fertile soil through annual flooding, which deposited nutrient-rich sediment essential for agriculture."
          },
          {
            id: "q3",
            type: "multiple-choice",
            question: "The Code of Hammurabi was significant because it:",
            options: [
              "Established the first democracy",
              "Created the first written laws",
              "Founded the first empire",
              "Developed the first calendar"
            ],
            correctAnswer: 1,
            explanation: "The Code of Hammurabi was one of the first written legal codes, establishing laws and punishments that applied to all social classes."
          }
        ]
      }
    },

    {
      id: "classical-civilizations",
      title: "Classical Civilizations: Greece and Rome",
      description: "The rise and influence of Greek and Roman civilizations on Western culture",
      order: 2,
      estimatedHours: 4.5,
      content: [
        {
          id: "greek-civilization",
          title: "Ancient Greece",
          description: "Greek contributions to philosophy, politics, art, and science",
          type: "video",
          duration: 35
        },
        {
          id: "roman-empire",
          title: "The Roman Empire",
          description: "Rise, expansion, and legacy of the Roman Empire",
          type: "text",
          duration: 50,
          textContent: `# The Roman Empire: Republic to Empire

## From City-State to World Power

### The Roman Republic (509-27 BCE)

**Origins and Foundation**
- Founded traditionally in 753 BCE by Romulus
- Monarchy overthrown in 509 BCE
- Location: Italian Peninsula, seven hills of Rome

**Government Structure**
- **Consuls**: Two chief executives elected annually
- **Senate**: Advisory body of former magistrates
- **Assemblies**: Popular assemblies for voting
- **Checks and Balances**: System to prevent concentration of power

**Social Structure**
- **Patricians**: Aristocratic families with political rights
- **Plebeians**: Common citizens, initially excluded from government
- **Slaves**: Large population with no rights
- **Clients**: Free people dependent on patrons

**Republican Expansion**
- **Italian Peninsula**: Gradual conquest and alliance system
- **Punic Wars** (264-146 BCE): Conflict with Carthage
- **Mediterranean Dominance**: "Mare Nostrum" (Our Sea)
- **Hellenistic Kingdoms**: Conquest of Greek territories

### Crisis of the Republic

**Internal Conflicts**
- **Social Wars**: Tensions between classes
- **Slave Revolts**: Spartacus rebellion (73-71 BCE)
- **Political Violence**: Use of force in politics
- **Military Commanders**: Armies loyal to generals, not state

**Key Figures in the Republic's End**
- **Marius and Sulla**: Competing generals and civil war
- **First Triumvirate**: Pompey, Caesar, Crassus alliance
- **Julius Caesar**: Dictator who crossed the Rubicon
- **Augustus**: First emperor, end of republic

### The Roman Empire (27 BCE-476/1453 CE)

**Augustus and the Principate**
- **Octavian becomes Augustus** (27 BCE)
- **Princeps**: "First Citizen" maintaining republican facade
- **Praetorian Guard**: Imperial bodyguard
- **Pax Romana**: Roman Peace lasting 200 years

**Imperial Administration**
- **Provinces**: Divided into senatorial and imperial
- **Governors**: Appointed administrators
- **Roman Law**: Legal system applied throughout empire
- **Citizenship**: Gradually extended to all free inhabitants

**Economic Foundations**
- **Agriculture**: Latifundia (large estates) worked by slaves
- **Trade**: Extensive commercial networks
- **Currency**: Standardized coinage system
- **Infrastructure**: Roads, aqueducts, ports

### Roman Culture and Society

**Religion**
- **Polytheism**: Adoption of Greek gods
- **Imperial Cult**: Worship of emperors
- **Mystery Religions**: Eastern cults (Mithraism, Christianity)
- **Christianity**: From persecution to state religion

**Education and Literature**
- **Greek Influence**: Adoption of Hellenistic culture
- **Latin Literature**: Virgil, Ovid, Cicero, Tacitus
- **Rhetoric**: Art of public speaking
- **Stoicism**: Popular philosophy emphasizing duty and virtue

**Entertainment**
- **Gladiatorial Games**: Public spectacles in amphitheaters
- **Chariot Racing**: Popular sport in circuses
- **Theater**: Comedies and tragedies
- **Bread and Circuses**: Policy to keep masses content

### Roman Engineering and Architecture

**Architectural Innovations**
- **Arch and Dome**: Revolutionary construction techniques
- **Concrete**: Durable building material
- **Aqueducts**: Water supply systems
- **Roads**: "All roads lead to Rome"

**Famous Structures**
- **Colosseum**: Largest amphitheater
- **Pantheon**: Temple with massive dome
- **Forum Romanum**: Center of political and commercial life
- **Baths of Caracalla**: Public bathing complexes

### Military Organization

**Legion Structure**
- **Professional Soldiers**: Career military service
- **Discipline and Training**: Rigorous preparation
- **Standardized Equipment**: Uniform weapons and armor
- **Engineering Corps**: Siege warfare and fortifications

**Military Tactics**
- **Testudo** (Tortoise): Shield formation
- **Siege Warfare**: Advanced techniques for capturing cities
- **Fortified Camps**: Temporary and permanent bases
- **Auxiliary Forces**: Non-citizen soldiers

### The Later Empire

**Division of the Empire**
- **Diocletian's Reforms** (284-305 CE): Administrative division
- **Constantinople** (330 CE): New eastern capital
- **Western vs. Eastern Empire**: Separate administrations

**Challenges and Decline**
- **Barbarian Invasions**: Germanic tribes pressure
- **Economic Problems**: Inflation, taxation, trade disruption
- **Military Issues**: Recruitment problems, civil wars
- **Religious Changes**: Rise of Christianity

**Fall of the Western Empire**
- **476 CE**: Last Western emperor deposed
- **Barbarian Kingdoms**: Germanic rule in former provinces
- **Eastern Empire Continues**: Byzantine Empire until 1453

### Roman Law and Legacy

**Legal Principles**
- **Twelve Tables** (450 BCE): First written Roman law
- **Civil Law**: Rights and duties of citizens
- **Natural Law**: Universal principles of justice
- **Corpus Juris Civilis**: Justinian's legal code

**Lasting Influences**
- **Legal Systems**: Foundation of many modern legal codes
- **Languages**: Latin roots in Romance languages
- **Government**: Republican and imperial models
- **Architecture**: Classical styles in public buildings
- **Christianity**: Spread throughout empire

### Comparison with Other Empires

**Similarities to Other Classical Empires**
- **Territorial Expansion**: Large geographic scope
- **Cultural Synthesis**: Blend of conquered peoples' traditions
- **Administrative Systems**: Efficient governance
- **Trade Networks**: Economic integration

**Unique Roman Features**
- **Legal Tradition**: Comprehensive law code
- **Engineering**: Advanced construction techniques
- **Military Organization**: Professional legions
- **Political Evolution**: Republic to empire transition

The Roman Empire's influence on Western civilization cannot be overstated. From legal principles to architectural styles, from language to government structures, Roman innovations continue to shape the modern world. Understanding Rome helps us appreciate how empires rise, adapt, and ultimately transform into new forms of civilization.`
        },
        {
          id: "empire-comparison",
          title: "Comparing Classical Empires",
          description: "Analyze similarities and differences between Greek, Roman, Persian, and Chinese empires",
          type: "arts-humanities-interactive",
          duration: 30,
          interactiveData: {
            exerciseType: "empire-comparison",
            instructions: "Compare different aspects of classical empires and analyze their methods of governance, cultural achievements, and reasons for success or decline.",
            empires: [
              {
                name: "Roman Empire",
                government: "Republic then autocracy",
                military: "Professional legions",
                culture: "Greek-influenced",
                decline: "Barbarian invasions, economic crisis"
              },
              {
                name: "Persian Empire",
                government: "Absolute monarchy",
                military: "Diverse ethnic units",
                culture: "Zoroastrianism, tolerance",
                decline: "Alexander's conquest"
              },
              {
                name: "Han China",
                government: "Imperial bureaucracy",
                military: "Peasant conscription",
                culture: "Confucian values",
                decline: "Regional warlords, economic strain"
              }
            ],
            hints: [
              "Consider how geography influenced each empire",
              "Look at how they dealt with cultural diversity",
              "Think about economic and military factors in their rise and fall"
            ]
          }
        }
      ],
      quiz: {
        id: "classical-civilizations-quiz",
        title: "Classical Civilizations Quiz",
        description: "Test your knowledge of Greek and Roman civilizations",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What was the main cause of the fall of the Roman Republic?",
            options: [
              "Barbarian invasions",
              "Economic collapse",
              "Civil wars between military leaders",
              "Natural disasters"
            ],
            correctAnswer: 2,
            explanation: "The Roman Republic fell due to civil wars between competing military leaders who commanded the loyalty of their armies rather than the state."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "Which aspect of Roman culture had the most lasting influence on Western civilization?",
            options: [
              "Military tactics",
              "Legal system",
              "Religious practices",
              "Art styles"
            ],
            correctAnswer: 1,
            explanation: "Roman law, codified in the Corpus Juris Civilis, became the foundation for legal systems throughout Europe and continues to influence modern law."
          }
        ]
      }
    },

    {
      id: "medieval-period",
      title: "Medieval Civilizations",
      description: "Europe, Islamic world, and Asian civilizations during the Middle Ages",
      order: 3,
      estimatedHours: 4.0,
      content: [
        {
          id: "feudalism-europe",
          title: "European Feudalism",
          description: "Medieval social, political, and economic systems in Europe",
          type: "video",
          duration: 35
        }
      ],
      quiz: {
        id: "medieval-quiz",
        title: "Medieval Civilizations Quiz",
        timeLimit: 20,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What was the primary basis of the feudal system?",
            options: ["Trade", "Land ownership", "Religious authority", "Military conquest"],
            correctAnswer: 1,
            explanation: "The feudal system was primarily based on land ownership, with lords granting land to vassals in exchange for military service and loyalty."
          }
        ]
      }
    },

    {
      id: "renaissance-reformation",
      title: "Renaissance and Reformation",
      description: "Cultural rebirth and religious transformation in Europe",
      order: 4,
      estimatedHours: 4.0,
      content: [
        {
          id: "renaissance-culture",
          title: "Renaissance Art and Culture",
          description: "Humanism, art, and scientific revolution in Renaissance Europe",
          type: "video",
          duration: 30
        }
      ],
      quiz: {
        id: "renaissance-quiz",
        title: "Renaissance and Reformation Quiz",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Who initiated the Protestant Reformation?",
            options: ["John Calvin", "Martin Luther", "Henry VIII", "Ulrich Zwingli"],
            correctAnswer: 1,
            explanation: "Martin Luther initiated the Protestant Reformation in 1517 with his 95 Theses challenging Catholic Church practices."
          }
        ]
      }
    },

    {
      id: "age-of-exploration",
      title: "Age of Exploration and Colonization",
      description: "European exploration and the establishment of global empires",
      order: 5,
      estimatedHours: 4.0,
      content: [
        {
          id: "exploration-motives",
          title: "Motives for Exploration",
          description: "Economic, religious, and political reasons for European expansion",
          type: "video",
          duration: 35
        }
      ],
      quiz: {
        id: "exploration-quiz",
        title: "Age of Exploration Quiz",
        timeLimit: 20,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What were the main motives for European exploration?",
            options: ["Gold, God, and Glory", "Land, Liberty, and Law", "Trade, Technology, and Territory", "Power, Prestige, and Progress"],
            correctAnswer: 0,
            explanation: "The main motives for European exploration were often summarized as Gold (economic gain), God (spreading Christianity), and Glory (national prestige)."
          }
        ]
      }
    },

    {
      id: "enlightenment-revolutions",
      title: "Enlightenment and Democratic Revolutions",
      description: "Intellectual movement and political revolutions of the 17th-18th centuries",
      order: 6,
      estimatedHours: 4.0,
      content: [
        {
          id: "enlightenment-ideas",
          title: "Enlightenment Philosophy",
          description: "Ideas of reason, natural rights, and scientific method",
          type: "video",
          duration: 30
        }
      ],
      quiz: {
        id: "enlightenment-quiz",
        title: "Enlightenment and Revolutions Quiz",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Which Enlightenment thinker wrote about the separation of powers?",
            options: ["John Locke", "Voltaire", "Montesquieu", "Rousseau"],
            correctAnswer: 2,
            explanation: "Montesquieu wrote about the separation of powers in government, influencing the structure of modern democratic governments."
          }
        ]
      }
    },

    {
      id: "industrial-revolution",
      title: "Industrial Revolution",
      description: "Transformation of production, society, and global economics",
      order: 7,
      estimatedHours: 4.0,
      content: [
        {
          id: "industrial-changes",
          title: "Technological and Social Changes",
          description: "Machinery, factories, and urbanization during industrialization",
          type: "video",
          duration: 35
        }
      ],
      quiz: {
        id: "industrial-quiz",
        title: "Industrial Revolution Quiz",
        timeLimit: 20,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Where did the Industrial Revolution begin?",
            options: ["France", "Germany", "Great Britain", "United States"],
            correctAnswer: 2,
            explanation: "The Industrial Revolution began in Great Britain in the late 18th century before spreading to other countries."
          }
        ]
      }
    },

    {
      id: "modern-world",
      title: "20th Century and the Modern World",
      description: "World wars, decolonization, and globalization",
      order: 8,
      estimatedHours: 4.0,
      content: [
        {
          id: "world-wars",
          title: "World Wars and Their Impact",
          description: "Global conflicts and their transformation of the modern world",
          type: "video",
          duration: 40
        }
      ],
      quiz: {
        id: "modern-world-quiz",
        title: "Modern World Quiz",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What event is generally considered the start of World War I?",
            options: ["Invasion of Poland", "Assassination of Archduke Franz Ferdinand", "Attack on Pearl Harbor", "Sinking of the Lusitania"],
            correctAnswer: 1,
            explanation: "The assassination of Archduke Franz Ferdinand of Austria-Hungary in 1914 is generally considered the trigger that started World War I."
          }
        ]
      }
    }
  ],

  assignments: [
    {
      id: "historical-research-project",
      title: "Historical Research and Analysis Project",
      description: "Conduct in-depth research on a historical topic using primary and secondary sources",
      type: "project",
      dueWeeks: 12,
      maxScore: 100,
      instructions: "Choose a specific historical topic, develop a research question, analyze primary and secondary sources, and present your findings in a well-structured historical argument.",
      submissionFormat: "pdf",
      resources: [
        "Historical databases",
        "Primary source collections",
        "Academic history journals",
        "Citation style guides"
      ]
    }
  ],

  finalExam: {
    id: "world-history-final-exam",
    title: "World History Final Examination",
    description: "Comprehensive examination covering major civilizations, historical developments, and analytical skills",
    timeLimit: 180,
    passingScore: 70,
    attempts: 2,
    questions: [
      {
        id: "final-q1",
        type: "multiple-choice",
        question: "Which factor was most important in the development of early river valley civilizations?",
        options: [
          "Mountain barriers for protection",
          "Access to ocean trade routes",
          "Fertile soil from river flooding",
          "Abundant mineral resources"
        ],
        correctAnswer: 2,
        explanation: "Fertile soil from annual river flooding was crucial for agricultural development, which supported large populations and allowed for the specialization that characterizes civilization."
      },
      {
        id: "final-q2",
        type: "multiple-choice",
        question: "The concept of the 'Mandate of Heaven' was most important in which civilization?",
        options: [
          "Roman Empire",
          "Persian Empire",
          "Chinese dynasties",
          "Egyptian kingdoms"
        ],
        correctAnswer: 2,
        explanation: "The Mandate of Heaven was a Chinese political and religious doctrine that justified the rule of dynasties and explained dynastic changes as the result of divine approval or disapproval."
      }
    ]
  },

  quizzes: []
};